/* eslint-disable no-console */
import { DateTime } from "luxon";

import {
	authInterceptorFactory,
	axios,
	extendTeam,
	interceptAndNormalizeResponse,
	piggyBack,
	requestHeaderNormalizerInterceptor
} from "@teaming/io-helpers";

import {
	growthGoalFactory,
	growthKeyResultFactory
} from "@teaming/common-gokr";

import {
	TeamMemberStatusType,
	TeamRoleType,
	TeamType
} from "@teaming/common-enums";

import { materializeOneOnOne } from "@teaming/common-ui/utils/teamUtil";
import { teamCache } from "../iocache";
let cachedSuggestedTopics = null;

const IO_TEAM_NAMESPACE = "team";

export default function ioTeamFactory( { config: { baseUri, }, authService, telemetry, i18n, ioGokr, ioUser, } ) {
	axios.defaults.baseURL = baseUri;
	axios.interceptors.request.use( requestHeaderNormalizerInterceptor );
	axios.interceptors.request.use( authInterceptorFactory( { authService, } ) );
	axios.interceptors.response.use( ...interceptAndNormalizeResponse() );
	const generateGrowthGoal = growthGoalFactory( { i18n, } );
	const generateGrowthKeyResult = growthKeyResultFactory( { i18n, } );

	const _fetchTeam = async ( { teamId, refetch = false, } ) => {
		let result = teamCache.get( teamId );
		if ( result && !refetch ) {
			return result;
		}
		result = await axios( {
			method: "get",
			url: `team/${ teamId }`,
			headers: {
				"x-api-version": 3, // eslint-disable-line no-magic-numbers
			},
		} );
		result.data = extendTeam( { team: result.data, } );
		teamCache.set( teamId, result );
		return result;
	};

	const _favoriteTeam = async ( { teamId, } ) => {
		const url = `/team/${ teamId }/favorite`;
		await axios( {
			method: "post",
			url,
			data: {},
		} );
		teamCache.remove( teamId );
	};

	const _unfavoriteTeam = async ( { teamId, } ) => {
		const url = `/team/${ teamId }/favorite`;
		await axios( {
			method: "delete",
			url,
			data: {},
		} );
		teamCache.remove( teamId );
	};

	const _changeTeamRoles = async ( {
		teamId,
		memberRoleMap,
	} ) => {
		teamCache.remove( teamId );

		const promises = Object.keys( memberRoleMap ).map( personId => {
			const role = memberRoleMap[ personId ];
			return axios( {
				method: "post",
				url: `team/${ teamId }/member/${ personId }`,
				data: { role, },
			} );
		} );

		await Promise.all( promises );
	};

	const teamMethods = {

		fetchTeam: piggyBack( async ( { teamId, refetch = false, } ) => {
			return _fetchTeam( { teamId, refetch, } );
		} ),

		async fetchTeamLead( { teamId, } ) {
			const result = await this.fetchTeam( { teamId, } );
			const { data: team, } = result;
			const lead = team.members.find( m => m.role === "LEAD" );
			return await ioUser.fetchUser( { personId: lead.personId, } );
		},

		async fetchTeamMemberDemographics( { personId, teamId, } ) {
			return await axios.get( `/team/${ teamId }/member/${ personId }/demographics` );
		},

		async inviteTeam( { teamId, members, skipTelemetry = false, } ) {
			await axios(
				{
					method: "post",
					url: `/team/${ teamId }/member`,
					headers: {
						"x-api-version": 3, // eslint-disable-line no-magic-numbers
					},
					data: {
						members,
					},
				}
			);

			teamCache.remove( teamId );
			const teamResponse = await this.fetchTeam( { teamId, } );

			const team = teamResponse.data;
			if ( !skipTelemetry ) {
				members.forEach( member => {
					telemetry.onboarding.teamMemberInvited( team.name, member.email );
				} );
			}

			return Promise.resolve( teamResponse );
		},

		async inviteDeferredMember( { teamId, member, skipTelemetry = false, } ) {
			const invitedMember = {
				...member,
				status: TeamMemberStatusType.INVITED,
			};
			return this.inviteTeam( { teamId, members: [ invitedMember, ], skipTelemetry, } );
		},

		async saveGrowthOpportunityInfo( {
			user: { personId, timezone, onboarding: { oneOnOneTeamId, }, },
			teamId,
			isLeadOnboarding,
			selectedOpportunity,
			otherOpportunityValue,
			// eslint-disable-next-line no-magic-numbers
			onboardingVersion = 3,
		} ) {
			const data = {
				growth: {
					selectedOpportunity,
					otherOpportunityValue,
				},
			};

			// if a user was not invited to a signup team, do not record demographics
			if ( teamId ) {
				await axios(
					{
						method: "post",
						url: `/team/${ teamId }/member/${ personId }/demographics`,
						data,
					}
				);
			}

			if ( isLeadOnboarding ) {
				telemetry.onboarding.lead.growthOpportunity( {
					growthOpportunity: selectedOpportunity,
					onboardingVersion,
				} );
			}

			if ( !isLeadOnboarding && oneOnOneTeamId ) {
				const growthGoal = generateGrowthGoal( {
					timezone,
					teamId: oneOnOneTeamId,
					opportunity: selectedOpportunity,
					onboardingVersion,
				} );
				const [ savedGrowthGoal, oneOnOneTeam, ] = await Promise.all( [
					ioGokr.saveGoal( { goal: growthGoal, } ),
					this.fetchOneOnOne( { teamId: oneOnOneTeamId, } ),
				] ).then( responses => {
					const [ saveGoalResponse, fetchTeamResponse, ] = responses;
					const fetchedTeam = fetchTeamResponse.data;
					teamCache.set( fetchedTeam.id, fetchTeamResponse );
					return [ saveGoalResponse.data, fetchTeamResponse.data, ];
				} );

				const { members, } = oneOnOneTeam;
				const lead = members.find( member => member.role === TeamRoleType.MANAGER );
				const growthKeyResult = generateGrowthKeyResult( {
					personId,
					leadId: lead.personId,
					goalId: savedGrowthGoal.id,
					opportunity: selectedOpportunity,
					onboardingVersion,
				} );

				await ioGokr.saveKeyResult( { keyResult: growthKeyResult, } );
			}
		},

		async saveOnboardingTeam( { team, leadId, } ) {
			await this.updateTeam( { team, members: team.members, } );
			await this.createOneOnOnes( { leadId, team, skipInviteEmail: true, } ).catch( err => {
				// until we have more error handling defined, we're going to silently fail if
				// a 1:1 create returns an error. FWIW, a 422 just means that 1:1 already exists (a
				// likely scenario for testing internally). Logging for now
				console.error( err ); // eslint-disable-line no-console
			} );
		},

		async saveTeamCharacteristics( { teamId, characteristics, } ) {
			await axios(
				{
					method: "post",
					url: `team/${ teamId }/characteristics`,
					data: characteristics,
				}
			);

			teamCache.remove( teamId );
		},

		async saveTeamGrowthOpportunityInfo( { personId, teamId, selectedOpportunity, } ) {
			const data = {
				teamGrowth: selectedOpportunity,
			};

			const response = await axios(
				{
					method: "post",
					url: `/team/${ teamId }/member/${ personId }/demographics`,
					data,
				}
			);

			telemetry.onboarding.lead.teamGrowthOpportunity( {
				growthOpportunity: selectedOpportunity,
			} );

			return response;
		},

		saveTeamMemberDemographics( { data, personId, teamId, } ) {
			return axios(
				{
					method: "post",
					url: `/team/${ teamId }/member/${ personId }/demographics`,
					data,
				}
			);
		},

		async createTeam( { team, } ) {
			return axios( {
				method: "post",
				url: "team",
				data: team,
			} );
		},

		async createAndFavoriteTeam( { team, } ) {
			const response = await axios( {
				method: "post",
				url: "team",
				data: team,
			} );

			const { data: createdTeam, } = response;

			await this.favoriteTeam( { teamId: createdTeam.id, } );

			return this.fetchTeam( { teamId: createdTeam.id, refetch: true, } );
		},

		async updateTeam( { team, members = [], } ) {
			const { id: teamId, } = team;

			const updateTeamNamePromise = axios( {
				method: "post",
				url: `team/${ teamId }`,
				data: team,
			} );

			// members
			const updateMemberPromises = members.map( member => {
				const { personId, role, } = member;
				return axios( {
					method: "post",
					url: `team/${ teamId }/member/${ personId }`,
					data: {
						role,
					},
				} );
			} );

			await Promise.all( [ updateTeamNamePromise, ...updateMemberPromises, ] );

			teamCache.remove( teamId );
		},

		async renameTeam( { teamId, name, } ) {
			return this.updateTeam( {
				team: {
					id: teamId,
					name,
				},
				members: [],
			} );
		},

		async changeTeamRoles( {
			teamId,
			memberRoleMap,
		} ) {
			return _changeTeamRoles( { teamId, memberRoleMap, } );
		},

		async removeTeamMember( { teamId, personId, } ) {
			return axios( {
				method: "delete",
				url: `team/${ teamId }/member/${ personId }`,
			} );
		},

		async leaveTeam( { teamId, } ) {
			const { personId, } = ( await ioUser.getUserProfile() ).data;
			return this.removeTeamMember( { teamId, personId, } );
		},

		async fetchMyTeammates() {
			return axios( {
				method: "get",
				url: "/user/me/teammates",
			} );
		},

		async favoriteTeam( { teamId, } ) {
			return _favoriteTeam( { teamId, } );
		},

		async unfavoriteTeam( { teamId, } ) {
			return _unfavoriteTeam( { teamId, } );
		},

		async fetchMyTeams( {
			offset,
			limit,
		} = {} ) {
			const params = Object.assign( {
				offset: offset || 0,
				// eslint-disable-next-line no-magic-numbers
				limit: limit || 10,
			} );

			// @todo: add paging to team endpoint, and exclude 1:1s
			const response = await axios( {
				method: "get",
				url: "/team",
				params,
			} );
			const { data: allTeams, metadata, } = response;

			const extendedTeams = allTeams.map( team => extendTeam( { team, } ) );

			for ( const team of extendedTeams ) {
				teamCache.set( team.id, {
					...response,
					data: team,
				} );
			}

			const myTeams = extendedTeams.filter( team => team.type === TeamType.TEAM );

			return Promise.resolve( {
				data: myTeams,
				error: null,
				metadata,
			} );
		},

		async fetchMyFavoriteTeams() {
			// @todo: add endpoint to actually fetch favorite teams
			const { data: myTeams, metadata, } = await this.fetchMyTeams();

			const myFavoriteTeams = myTeams.filter( team => team.isFavorited );

			return Promise.resolve( {
				data: myFavoriteTeams,
				error: null,
				metadata,
			} );
		},
	};

	const oneOnOneMethods = {
		async fetchMyOneOnOnes( {
			offset,
			limit,
		} = {} ) {
			const params = Object.assign( {
				offset: offset || 0,
				// eslint-disable-next-line no-magic-numbers
				limit: limit || 10,
			} );

			// @todo: implement /one-on-ones endpoint, with paging
			const response = await axios( {
				method: "get",
				url: "/team",
				params,
			} );
			const { data: allTeams, metadata, } = response;

			const extendedTeams = allTeams.map( team => extendTeam( { team, } ) );

			for ( const team of extendedTeams ) {
				teamCache.set( team.id, {
					...response,
					data: team,
				} );
			}

			const myOneOnOnes = extendedTeams.filter( team => team.type === TeamType.ONE_ON_ONE );

			return Promise.resolve( {
				data: myOneOnOnes,
				error: null,
				metadata,
			} );
		},

		async fetchMyFavoriteOneOnOnes() {
			// @todo: add endpoint to actually fetch favorite 1:1s
			const { data: myOneOnOnes, metadata, } = await this.fetchMyOneOnOnes();

			const myFavoriteOneOnOnes = myOneOnOnes.filter( oneOnOne => oneOnOne.isFavorited );

			return Promise.resolve( {
				data: myFavoriteOneOnOnes,
				error: null,
				metadata,
			} );
		},

		async createOneOnOnes( { leadId, team, skipInviteEmail, } ) {
			const { members, } = team;
			const manager = members.find( member => member.personId === leadId );
			const reports = members.filter( member => member.personId !== leadId );

			const apiPromises = reports.map( report => {
				const oneOnOne = materializeOneOnOne( {
					initiator: manager,
					invitee: report,
					team,
					skipInviteEmail,
				} );

				return this.createAndFavoriteOneOnOne( { oneOnOne, } );
			} );

			return await Promise.all( apiPromises );
		},

		fetchOneOnOne( { teamId, refetch = false, } ) {
			return _fetchTeam( { teamId, refetch, } );
		},

		async createOneOnOne( { oneOnOne, } ) {
			return axios( {
				method: "post",
				url: "team",
				data: oneOnOne,
			} );
		},

		async createAndFavoriteOneOnOne( { oneOnOne, } ) {
			const response = await axios( {
				method: "post",
				url: "team",
				data: oneOnOne,
			} );

			const { data: createdOneOnOne, } = response;

			await this.favoriteOneOnOne( { oneOnOneId: createdOneOnOne.id, } );

			return this.fetchOneOnOne( { teamId: createdOneOnOne.id, refetch: true, } );
		},

		async favoriteOneOnOne( { oneOnOneId, } ) {
			return _favoriteTeam( { teamId: oneOnOneId, } );
		},

		async unfavoriteOneOnOne( { oneOnOneId, } ) {
			return _unfavoriteTeam( { teamId: oneOnOneId, } );
		},

		async fetchSuggestedTopics() {
			if ( !cachedSuggestedTopics ) {
				cachedSuggestedTopics = ( await axios.get( "/team/meeting/suggested-topics" ) ).data;
			}

			return Promise.resolve( [ ...cachedSuggestedTopics, ] );
		},

		async archiveOneOnOne( { id, name, } ) {
			teamCache.remove( id );

			const data = {
				name,
				// eslint-disable-next-line no-magic-numbers
				archived: DateTime.utc().toUnixInteger() * 1000,
			};

			await axios( {
				method: "post",
				url: `team/${ id }`,
				data,
			} );

			await _unfavoriteTeam( { teamId: id, } );
		},

		async unarchiveOneOnOne( { id, name, } ) {
			teamCache.remove( id );

			const data = {
				name,
				archived: 0,
			};

			await axios( {
				method: "post",
				url: `team/${ id }`,
				data,
			} );
		},

		async changeOneOnOneRoles( {
			teamId,
			memberRoleMap,
		} ) {
			return _changeTeamRoles( { teamId, memberRoleMap, } );
		},
	};

	const fetchAllMyTeams = async ( {
		offset,
		limit,
	} = {} ) => {
		const params = Object.assign( {
			offset: offset || 0,
			// eslint-disable-next-line no-magic-numbers
			limit: limit || 10,
		} );

		// @todo: add paging to team endpoint, and exclude 1:1s
		const response = await axios( {
			method: "get",
			url: "/team",
			params,
		} );
		const { data: allTeams, metadata, } = response;

		const extendedTeams = allTeams.map( team => extendTeam( { team, } ) );

		for ( const team of extendedTeams ) {
			teamCache.set( team.id, {
				...response,
				data: team,
			} );
		}

		return Promise.resolve( {
			data: extendedTeams,
			error: null,
			metadata,
		} );
	};

	const resendInvitation = async ( { teamId, email, } ) => {
		const url = `/team/${ teamId }/invitation`;
		return axios.post( url, {
			email,
		} );
	};

	const fetchGoalTeam = async ( { goalId, refetch = false, } ) => {
		const { data: goal, } = await ioGokr.fetchGoal( { goalId, } );
		const { teamId, } = goal;
		return _fetchTeam( { teamId, refetch, } );
	};

	return {
		namespace: IO_TEAM_NAMESPACE,
		...teamMethods,
		...oneOnOneMethods,
		fetchAllMyTeams,
		fetchGoalTeam,
		resendInvitation,
	};
}
